/**
 * @fileoverview The page's telemetry-consent posture (UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-13).
 *
 * The browser sends two kinds of telemetry, and consent governs only one of them:
 *
 * - **Errors and log lines** — uncaught errors, unhandled rejections, WARNING+ lines. These are
 *   the site operator's record of its OWN software failing. They set no cookie, read no storage,
 *   carry no identifier beyond a per-page random trace id, and are re-redacted by the ingest. They
 *   ship on every surface, consent or not — they are what keeps a public page working.
 * - **Web vitals** — measurements of the visitor's experience (LCP, INP, CLS, FCP, TTFB). The
 *   ecosystem's standing doctrine puts experience measurement on a public surface behind the
 *   visitor's consent (`@puffin-core/marketing-shell`'s `webVitalsAdapter`: "the adapter is the
 *   consent boundary"), and this module is how the one collector honours it.
 *
 * A page is GOVERNED once a consent surface reports a decision for it. There are two, both in
 * `@puffin-core/marketing-shell`: `<CookieConsentProvider>` reports on mount (the stored decision, or PENDING
 * when the visitor has not answered yet) and on every accept / decline, wherever it is rendered (every
 * marketing site, public storefronts, customer portals); `<ConsentWithheldProvider>` — a tenant site with no
 * privacy notice, which cannot ask — reports DECLINED on mount (F-SG-3). An ungoverned page (the authenticated apps, which carry no consent banner) is not asked.
 * The state is a page singleton for the same reason the logger is: the provider that decides and the
 * collector that obeys are mounted by different code at different times, and one page has one posture.
 *
 * A decision is one of THREE (F-SG-2): PENDING (the banner is up, unanswered), DECLINED, GRANTED. A
 * boolean could not say PENDING, so "no answer yet" was reported as `false` and read as a decline: the
 * collector dropped the vitals it had held since before the banner mounted, and a first-visit Accept
 * released an empty hold — TTFB and FCP were lost for every visitor who accepted on their first visit.
 *
 * @module telemetryConsent
 */

/** The three decisions a consent surface can report. */
export const TELEMETRY_CONSENT = Object.freeze({
  PENDING: 'pending',
  DECLINED: 'declined',
  GRANTED: 'granted',
});

const DECISIONS = Object.values(TELEMETRY_CONSENT);

let governed = false;
let decision = null;
const listeners = new Set();

function snapshot() {
  return { governed, decision };
}

function notify() {
  const state = snapshot();
  for (const listener of listeners) {
    try {
      listener(state);
    } catch (_listenerErr) {
      // One broken subscriber must not stop the consent decision reaching the others.
    }
  }
}

/**
 * Record the consent surface's decision for this page, which also marks the page GOVERNED — only a
 * consent surface reports one.
 * @param {'pending'|'declined'|'granted'} value - one of {@link TELEMETRY_CONSENT}
 * @throws {TypeError} for anything outside the vocabulary — a surface that cannot name its decision
 *   is a programming error, never a silent withhold
 */
export function setTelemetryConsent(value) {
  if (!DECISIONS.includes(value)) {
    throw new TypeError(
      `setTelemetryConsent expects one of ${DECISIONS.join(' | ')}; received ${String(value)}`,
    );
  }
  governed = true;
  decision = value;
  notify();
}

/**
 * @returns {{governed: boolean, decision: ('pending'|'declined'|'granted'|null)}} the page's posture
 *   right now; `decision` is `null` while the page is ungoverned
 */
export function getTelemetryConsent() {
  return snapshot();
}

/**
 * Subscribe to posture changes.
 * @param {(state: {governed: boolean, decision: ('pending'|'declined'|'granted'|null)}) => void} listener
 * @returns {() => void} unsubscribe
 */
export function onTelemetryConsentChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Back to an ungoverned page with no subscribers — a test seam, like `resetLoggerInstance`.
 */
export function resetTelemetryConsent() {
  governed = false;
  decision = null;
  listeners.clear();
}
